// frontend/src/pages/CreateTask.jsx - Working Translation
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { 
    FaPlus, FaTrash, FaCopy, FaEye, FaPaperPlane, 
    FaUsers, FaCalendarAlt, FaFlag, FaClock, FaTag,
    FaFileUpload, FaTimes, FaUserPlus, FaCheckCircle,
    FaLanguage, FaMagic
} from 'react-icons/fa';

const CreateTask = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [frequency, setFrequency] = useState('One Time');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [isHindiMode, setIsHindiMode] = useState(false);
    const [translating, setTranslating] = useState(false);
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        assignedTo: [],
        department: '',
        priority: 'Medium',
        frequency: 'One Time',
        dueDate: '',
        startDate: '',
        daysCount: 7,
        dayOfWeek: 'Monday',
        weeksCount: 4,
        dayOfMonth: 1,
        monthsCount: 3,
        tags: [],
        estimatedHours: '',
        attachments: []
    });

    const [newTag, setNewTag] = useState('');

    // FIXED: Using fetch instead of axios (no Authorization header)
    const convertToHindi = async (text) => {
        if (!text || !isHindiMode) return text;
        
        setTranslating(true);
        try {
            const response = await fetch(
                `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=hi&dt=t&q=${encodeURIComponent(text)}`
            );
            
            const data = await response.json();
            if (data && data[0] && data[0][0] && data[0][0][0]) {
                return data[0][0][0];
            }
            return text;
        } catch (error) {
            console.error('Translation error:', error);
            toast.error('Translation failed. Using original text.');
            return text;
        } finally {
            setTranslating(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchTemplates();
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setFormData(prev => ({ ...prev, dueDate: tomorrow.toISOString().split('T')[0] }));
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get('/api/users');
            setUsers(response.data.users || []);
        } catch (error) {
            toast.error('Failed to load users');
            setUsers([]);
        }
    };

    const fetchTemplates = async () => {
        try {
            const response = await axios.get('/api/templates');
            setTemplates(response.data.templates || []);
        } catch (error) {
            console.error('Failed to load templates');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title || !formData.description) {
            toast.error('Please fill task title and description');
            return;
        }

        if (formData.assignedTo.length === 0) {
            toast.error('Please assign at least one user');
            return;
        }

        if (frequency === 'One Time' && !formData.dueDate) {
            toast.error('Due date is required');
            return;
        }

        setLoading(true);
        try {
            const promises = formData.assignedTo.map(assignedUser => {
                const taskData = { 
                    ...formData, 
                    frequency,
                    assignedTo: assignedUser.mobile,
                    assignedName: assignedUser.name
                };
                return axios.post('/api/tasks', taskData);
            });
            
            const responses = await Promise.all(promises);
            const allSuccess = responses.every(res => res.data.success);
            
            if (allSuccess) {
                toast.success(`${responses.length} task(s) created successfully!`);
                navigate('/tasks');
            } else {
                toast.error('Some tasks failed to create');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create task');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleTitleConvert = async () => {
        if (formData.title) {
            const converted = await convertToHindi(formData.title);
            setFormData(prev => ({ ...prev, title: converted }));
            toast.success('Title converted to Hindi');
        }
    };

    const handleDescConvert = async () => {
        if (formData.description) {
            const converted = await convertToHindi(formData.description);
            setFormData(prev => ({ ...prev, description: converted }));
            toast.success('Description converted to Hindi');
        }
    };

    const handleBothConvert = async () => {
        await handleTitleConvert();
        await handleDescConvert();
    };

    const handleUserSelect = (userId, userName, userMobile) => {
        const isSelected = formData.assignedTo.some(u => u.id === userId);
        if (isSelected) {
            setFormData(prev => ({
                ...prev,
                assignedTo: prev.assignedTo.filter(u => u.id !== userId)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                assignedTo: [...prev.assignedTo, { id: userId, name: userName, mobile: userMobile }]
            }));
        }
    };

    const handleSelectAll = () => {
        if (formData.assignedTo.length === users.length) {
            setFormData(prev => ({ ...prev, assignedTo: [] }));
        } else {
            setFormData(prev => ({
                ...prev,
                assignedTo: users.map(u => ({ id: u.id, name: u.name, mobile: u.mobile }))
            }));
        }
    };

    const addTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const removeTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const applyTemplate = (template) => {
        setFormData(prev => ({
            ...prev,
            title: template.title,
            description: template.description,
            department: template.department || prev.department,
            priority: template.priority || prev.priority,
            frequency: template.frequency || prev.frequency
        }));
        setFrequency(template.frequency || 'One Time');
        setSelectedTemplate(template.id);
        toast.success(`Template "${template.title}" applied`);
    };

    const toggleHindiMode = () => {
        setIsHindiMode(!isHindiMode);
        toast.success(isHindiMode ? 'English mode activated' : 'Hindi mode activated - Type English, click Convert');
    };

    const getFrequencyFields = () => {
        switch (frequency) {
            case 'One Time':
                return (
                    <div className="mb-4" key="one-time">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <FaCalendarAlt className="inline mr-2" /> Due Date *
                        </label>
                        <input
                            type="date"
                            name="dueDate"
                            value={formData.dueDate}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            required
                        />
                    </div>
                );
            case 'Daily':
                return (
                    <div key="daily">
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                <FaCalendarAlt className="inline mr-2" /> Start Date *
                            </label>
                            <input
                                type="date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Number of Days
                            </label>
                            <input
                                type="number"
                                name="daysCount"
                                value={formData.daysCount}
                                onChange={handleChange}
                                min="1"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Sundays and holidays will be skipped automatically</p>
                        </div>
                    </div>
                );
            case 'Weekly':
                return (
                    <div key="weekly">
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                <FaCalendarAlt className="inline mr-2" /> Start Date *
                            </label>
                            <input
                                type="date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Day of Week
                            </label>
                            <select
                                name="dayOfWeek"
                                value={formData.dayOfWeek}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="Monday">Monday</option>
                                <option value="Tuesday">Tuesday</option>
                                <option value="Wednesday">Wednesday</option>
                                <option value="Thursday">Thursday</option>
                                <option value="Friday">Friday</option>
                                <option value="Saturday">Saturday</option>
                                <option value="Sunday">Sunday</option>
                            </select>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Number of Weeks
                            </label>
                            <input
                                type="number"
                                name="weeksCount"
                                value={formData.weeksCount}
                                onChange={handleChange}
                                min="1"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                    </div>
                );
            case 'Monthly':
                return (
                    <div key="monthly">
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                <FaCalendarAlt className="inline mr-2" /> Start Date *
                            </label>
                            <input
                                type="date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Day of Month
                            </label>
                            <input
                                type="number"
                                name="dayOfMonth"
                                value={formData.dayOfMonth}
                                onChange={handleChange}
                                min="1"
                                max="31"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Number of Months
                            </label>
                            <input
                                type="number"
                                name="monthsCount"
                                value={formData.monthsCount}
                                onChange={handleChange}
                                min="1"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="max-w-5xl mx-auto p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Create New Task</h1>
                    <p className="text-gray-500 mt-1">Create and assign tasks to multiple users</p>
                </div>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={toggleHindiMode}
                        className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                            isHindiMode 
                                ? 'bg-orange-500 text-white' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaLanguage /> {isHindiMode ? 'हिंदी मोड' : 'Hindi Mode'}
                    </button>
                    
                    <button
                        type="button"
                        onClick={() => setShowPreview(!showPreview)}
                        className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all flex items-center gap-2"
                    >
                        <FaEye /> {showPreview ? 'Hide Preview' : 'Show Preview'}
                    </button>
                </div>
            </div>

            {isHindiMode && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                        <FaMagic className="text-blue-600" />
                        <span className="text-sm font-semibold text-blue-800">
                            Hindi Mode Active - Type English, then click Convert
                        </span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                        Example: "I want my money back" → "मैं अपना पैसा वापस चाहता हूँ"
                    </p>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className={`${showPreview ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
                    <form onSubmit={handleSubmit} className="glass-card p-6">
                        {templates.length > 0 && (
                            <div className="mb-6 p-4 bg-purple-50 rounded-lg">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    <FaCopy className="inline mr-2" /> Use Template
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {templates.slice(0, 5).map(template => (
                                        <button
                                            key={template.id}
                                            type="button"
                                            onClick={() => applyTemplate(template)}
                                            className={`px-3 py-1 rounded-lg text-sm transition-all ${
                                                selectedTemplate === template.id
                                                    ? 'bg-purple-500 text-white'
                                                    : 'bg-white border border-purple-300 text-purple-600 hover:bg-purple-100'
                                            }`}
                                        >
                                            {template.title}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Title */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Task Title *
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder={isHindiMode ? "Type English here..." : "Enter task title"}
                                    required
                                />
                                {isHindiMode && formData.title && (
                                    <button
                                        type="button"
                                        onClick={handleTitleConvert}
                                        disabled={translating}
                                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
                                    >
                                        <FaMagic /> {translating ? 'Converting...' : 'Convert'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Description *
                            </label>
                            <div className="flex gap-2">
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="4"
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder={isHindiMode ? "Type English here..." : "Enter task description"}
                                    required
                                />
                                {isHindiMode && formData.description && (
                                    <button
                                        type="button"
                                        onClick={handleDescConvert}
                                        disabled={translating}
                                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 self-start whitespace-nowrap disabled:opacity-50"
                                    >
                                        <FaMagic /> {translating ? 'Converting...' : 'Convert'}
                                    </button>
                                )}
                            </div>
                            {isHindiMode && (formData.title || formData.description) && (
                                <button
                                    type="button"
                                    onClick={handleBothConvert}
                                    disabled={translating}
                                    className="mt-2 px-4 py-1 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 disabled:opacity-50"
                                >
                                    Convert Both to Hindi
                                </button>
                            )}
                        </div>

                        {/* Multi-Select Users - Keep your existing code */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                <FaUsers className="inline mr-2" /> Assign To (Multiple) *
                            </label>
                            <div className="border border-gray-300 rounded-lg p-3">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-gray-500">
                                        Selected: {formData.assignedTo.length} user(s)
                                    </span>
                                    <button
                                        type="button"
                                        onClick={handleSelectAll}
                                        className="text-sm text-purple-600 hover:text-purple-700"
                                    >
                                        {formData.assignedTo.length === users.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="max-h-40 overflow-y-auto space-y-2">
                                    {users.map(userItem => (
                                        <label key={userItem.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="checkbox"
                                                checked={formData.assignedTo.some(u => u.id === userItem.id)}
                                                onChange={() => handleUserSelect(userItem.id, userItem.name, userItem.mobile)}
                                                className="w-4 h-4 rounded border-gray-300 focus:ring-purple-500"
                                            />
                                            <span className="flex-1">
                                                <span className="font-medium">{userItem.name}</span>
                                                <span className="text-sm text-gray-500 ml-2">({userItem.mobile})</span>
                                            </span>
                                            <span className="text-xs text-gray-400">{userItem.department}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {formData.assignedTo.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {formData.assignedTo.map(user => (
                                        <span key={user.id} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                                            {user.name}
                                            <button
                                                type="button"
                                                onClick={() => handleUserSelect(user.id, user.name, user.mobile)}
                                                className="hover:text-purple-900"
                                            >
                                                <FaTimes size={10} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Department */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Department
                            </label>
                            <input
                                type="text"
                                name="department"
                                value={formData.department}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="Enter department"
                            />
                        </div>

                        {/* Tags */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                <FaTag className="inline mr-2" /> Tags
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="Add tag..."
                                />
                                <button
                                    type="button"
                                    onClick={addTag}
                                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                                >
                                    <FaPlus />
                                </button>
                            </div>
                            {formData.tags.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {formData.tags.map(tag => (
                                        <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                                            #{tag}
                                            <button
                                                type="button"
                                                onClick={() => removeTag(tag)}
                                                className="hover:text-red-500"
                                            >
                                                <FaTimes size={10} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Frequency */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                <FaClock className="inline mr-2" /> Frequency
                            </label>
                            <select
                                value={frequency}
                                onChange={(e) => setFrequency(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="One Time">One Time</option>
                                <option value="Daily">Daily</option>
                                <option value="Weekly">Weekly</option>
                                <option value="Monthly">Monthly</option>
                            </select>
                        </div>

                        {getFrequencyFields()}

                        {/* Priority */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                <FaFlag className="inline mr-2" /> Priority
                            </label>
                            <select
                                name="priority"
                                value={formData.priority}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                            </select>
                        </div>

                        {/* Estimated Hours */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Estimated Hours
                            </label>
                            <input
                                type="number"
                                name="estimatedHours"
                                value={formData.estimatedHours}
                                onChange={handleChange}
                                min="0"
                                step="0.5"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="Estimated time to complete"
                            />
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-4 mt-6">
                            <button
                                type="button"
                                onClick={() => navigate('/tasks')}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-gradient px-6 py-2 rounded-lg text-white font-semibold disabled:opacity-50 flex items-center gap-2"
                            >
                                {loading ? (
                                    'Creating...'
                                ) : (
                                    <>
                                        <FaPaperPlane /> Create Task{formData.assignedTo.length > 1 ? 's' : ''} ({formData.assignedTo.length})
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Preview Panel */}
                {showPreview && (
                    <div className="lg:col-span-1">
                        <div className="glass-card p-6 sticky top-6">
                            <h2 className="text-lg font-bold text-gray-800 mb-4">Task Preview</h2>
                            
                            <div className="space-y-3">
                                <div>
                                    <span className="text-xs text-gray-500">Title</span>
                                    <p className="font-medium">{formData.title || 'Not set'}</p>
                                </div>
                                
                                <div>
                                    <span className="text-xs text-gray-500">Description</span>
                                    <p className="text-sm text-gray-600">{formData.description || 'Not set'}</p>
                                </div>
                                
                                <div>
                                    <span className="text-xs text-gray-500">Assignees</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {formData.assignedTo.length > 0 ? (
                                            formData.assignedTo.map(user => (
                                                <span key={user.id} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                                                    <FaUserPlus size={10} /> {user.name}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-sm text-gray-400">No users assigned</span>
                                        )}
                                    </div>
                                </div>
                                
                                <div>
                                    <span className="text-xs text-gray-500">Priority</span>
                                    <p className={`inline-block px-2 py-1 rounded text-xs ${
                                        formData.priority === 'High' ? 'priority-high' :
                                        formData.priority === 'Medium' ? 'priority-medium' : 'priority-low'
                                    }`}>
                                        {formData.priority}
                                    </p>
                                </div>
                                
                                <div>
                                    <span className="text-xs text-gray-500">Due Date</span>
                                    <p>{formData.dueDate || 'Not set'}</p>
                                </div>
                                
                                {formData.tags.length > 0 && (
                                    <div>
                                        <span className="text-xs text-gray-500">Tags</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {formData.tags.map(tag => (
                                                <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                                                    #{tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {formData.estimatedHours && (
                                    <div>
                                        <span className="text-xs text-gray-500">Estimated Hours</span>
                                        <p>{formData.estimatedHours} hours</p>
                                    </div>
                                )}
                            </div>
                            
                            <div className="mt-4 pt-3 border-t">
                                <p className="text-xs text-green-600 flex items-center gap-1">
                                    <FaCheckCircle /> Will create {formData.assignedTo.length} task(s)
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreateTask;